/**
 * Alert thresholds (KAN-81).
 *
 * This screen *is* KAN-81's first acceptance criterion: Finance changes a value
 * here and the engine uses it on the next pass, with nothing to deploy.
 *
 * ## Money is entered in dollars and stored in cents
 *
 * The API speaks minor units, as everything in this codebase does. The form
 * speaks dollars, because Finance does. The conversion happens once, at the
 * input, and is the single most dangerous line on this page — a threshold typed
 * as 2500 meaning dollars but stored as 2500 cents would under-alert by a
 * factor of a hundred and look like it was working perfectly. The input shows
 * the unit beside it for the same reason.
 *
 * ## A rule with no module
 *
 * `cash_shortage` and `budget_variance` have nothing to evaluate them yet, and
 * the screen says so and names the ticket rather than showing a rule that
 * appears fine and silently never fires. Their thresholds are still editable —
 * Finance can set the value now and it takes effect the day the module lands.
 * That is the same treatment the executive dashboard gives its five unbuilt
 * tiles.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ALERT_SEVERITIES,
  ALERT_SEVERITY_META,
  COMPARISON_OPERATOR_LABELS,
  ROLES,
  ROLE_LABELS,
  type AlertCode,
  type AlertSeverity,
  type ComparisonOperator,
  type Role,
  type ThresholdUnit,
} from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { Alert as Banner } from '../ui/Alert'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'

interface RuleRow {
  code: AlertCode
  label: string
  description: string
  unit: ThresholdUnit
  permission: string
  moduleTicket: string
  available: boolean
  enabled: boolean
  threshold: number
  defaultThreshold: number
  operator: ComparisonOperator
  severity: AlertSeverity
  recipientRoles: Role[]
  cooldownMinutes: number
  digest: boolean
  updatedAt: string
  updatedBy: string | null
}

/** Cooldowns are offered as choices rather than as a free number of minutes. */
const COOLDOWNS: { minutes: number; label: string }[] = [
  { minutes: 60, label: 'Hourly' },
  { minutes: 240, label: 'Every 4 hours' },
  { minutes: 720, label: 'Twice a day' },
  { minutes: 1_440, label: 'Daily' },
  { minutes: 4_320, label: 'Every 3 days' },
  { minutes: 10_080, label: 'Weekly' },
]

export function AlertSettings() {
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState<AlertCode | null>(null)

  const rules = useQuery({
    queryKey: ['alerts', 'rules'],
    queryFn: () => apiFetch<{ rules: RuleRow[] }>('/alerts/rules'),
  })

  const save = useMutation({
    mutationFn: ({ code, patch }: { code: AlertCode; patch: Partial<RuleRow> }) =>
      apiFetch(`/alerts/rules/${code}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: (_data, variables) => {
      setSaved(variables.code)
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  return (
    <>
      <PageHeader
        title="Alert thresholds"
        description="What the firm is told about, and when. Changes take effect on the next evaluation pass — there is nothing to deploy."
        actions={
          <Link to="/alerts">
            <Button>Back to alerts</Button>
          </Link>
        }
      />

      <Banner tone="info" className="mb-6">
        Every threshold below is a <strong>starting value awaiting Finance</strong> rather than a
        policy. They are chosen to be defensible, not measured — the reasoning for each is in the
        rule's description, and KAN-81 is not done until these have been reviewed against matters
        Finance already knows to be a problem.
      </Banner>

      {save.isError && (
        <Banner tone="error" className="mb-4">
          {(save.error as Error).message}
        </Banner>
      )}

      <div className="space-y-4">
        {rules.isLoading && <p className="text-sm text-ink-600">Loading…</p>}

        {rules.data?.rules.map((rule) => (
          <RuleCard
            key={rule.code}
            rule={rule}
            saved={saved === rule.code}
            saving={save.isPending}
            onSave={(patch) => save.mutate({ code: rule.code, patch })}
          />
        ))}
      </div>
    </>
  )
}

function RuleCard({
  rule,
  saved,
  saving,
  onSave,
}: {
  rule: RuleRow
  saved: boolean
  saving: boolean
  onSave: (patch: Partial<RuleRow>) => void
}) {
  const [threshold, setThreshold] = useState(() => toDisplay(rule.unit, rule.threshold))
  const [severity, setSeverity] = useState(rule.severity)
  const [cooldown, setCooldown] = useState(rule.cooldownMinutes)
  const [roles, setRoles] = useState<Role[]>(rule.recipientRoles)
  const [digest, setDigest] = useState(rule.digest)

  const parsed = fromDisplay(rule.unit, threshold)
  const invalid = parsed === null

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {rule.label}
            {!rule.available ? (
              <Badge tone="neutral">Not built yet — {rule.moduleTicket}</Badge>
            ) : rule.enabled ? (
              <Badge tone="positive">On</Badge>
            ) : (
              <Badge tone="neutral">Off</Badge>
            )}
          </span>
        }
        description={rule.description}
        actions={
          <Button
            variant={rule.enabled ? 'secondary' : 'primary'}
            size="sm"
            disabled={!rule.available || saving}
            title={
              rule.available
                ? undefined
                : `No module calculates this yet — ${rule.moduleTicket} builds it.`
            }
            onClick={() => onSave({ enabled: !rule.enabled })}
          >
            {rule.enabled ? 'Turn off' : 'Turn on'}
          </Button>
        }
      />
      <CardBody>
        {!rule.available && (
          <Banner tone="warning" className="mb-4">
            No module on this platform calculates this figure yet, so the rule cannot fire.{' '}
            <strong>{rule.moduleTicket}</strong> builds it. The threshold can be set now and will
            take effect the day that lands — writing the calculation into the alert engine instead
            would make it the firm's de facto definition of the figure, and the real module would
            later disagree.
          </Banner>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            id={`${rule.code}-threshold`}
            label="Alert when the value"
            hint={`${COMPARISON_OPERATOR_LABELS[rule.operator]} — ${unitHint(rule.unit)}`}
          >
            <TextInput
              id={`${rule.code}-threshold`}
              value={threshold}
              inputMode="decimal"
              aria-invalid={invalid}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </Field>

          <Field
            id={`${rule.code}-severity`}
            label="Severity"
            hint={ALERT_SEVERITY_META[severity].description}
          >
            <Select
              id={`${rule.code}-severity`}
              value={severity}
              onChange={(e) => setSeverity(e.target.value as AlertSeverity)}
            >
              {ALERT_SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {ALERT_SEVERITY_META[value].label}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            id={`${rule.code}-cooldown`}
            label="Re-notify at most"
            hint="How often a condition that stays true is announced again. This is what stops one problem becoming twenty-four emails."
          >
            <Select id={`${rule.code}-cooldown`} value={cooldown} onChange={(e) => setCooldown(Number(e.target.value))}>
              {COOLDOWNS.map((option) => (
                <option key={option.minutes} value={option.minutes}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          {/**
            * A fieldset rather than a `Field`: this is a checkbox group, and
            * `Field` associates one label with one control. A label pointing at
            * a group is what a legend is for.
            */}
          <fieldset>
            <legend className="block text-sm font-medium text-ink-800">Notify</legend>
            <div className="flex flex-col gap-1 pt-1">
              {ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={roles.includes(role)}
                    onChange={(e) =>
                      setRoles((current) =>
                        e.target.checked
                          ? [...current, role]
                          : current.filter((value) => value !== role)
                      )
                    }
                  />
                  {ROLE_LABELS[role]}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-ink-600">
              Anybody here who does not hold the grant for the underlying data is filtered out at
              delivery.
            </p>
          </fieldset>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 pt-4">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={digest}
              onChange={(e) => setDigest(e.target.checked)}
            />
            Batch these into a digest rather than sending each one
          </label>

          <div className="flex items-center gap-3">
            <span className="text-xs text-ink-500">
              {rule.updatedBy
                ? `Changed by ${rule.updatedBy}, ${formatDateTime(rule.updatedAt)}`
                : `Default value (${toDisplay(rule.unit, rule.defaultThreshold)})`}
            </span>
            {saved && <Badge tone="positive">Saved</Badge>}
            <Button
              variant="primary"
              size="sm"
              disabled={invalid || saving || roles.length === 0}
              onClick={() =>
                onSave({
                  threshold: parsed!,
                  severity,
                  cooldownMinutes: cooldown,
                  recipientRoles: roles,
                  digest,
                })
              }
            >
              Save
            </Button>
          </div>
        </div>

        {invalid && (
          <p className="mt-2 text-sm text-red-700">
            {unitHint(rule.unit)} — enter a number, and not a negative one.
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * Stored value to what the form shows.
 *
 * Money divides by a hundred and everything else does not. Keeping the two
 * conversions adjacent and tiny is deliberate: this is the one place a factor
 * of a hundred can go missing, and a bug here produces a threshold that is
 * wrong by two orders of magnitude while looking entirely plausible.
 */
function toDisplay(unit: ThresholdUnit, value: number): string {
  if (unit === 'money') return (value / 100).toFixed(2)
  if (unit === 'bps') return (value / 100).toFixed(1)
  return String(value)
}

/** What the form shows to the stored value. Null when it is not a number. */
function fromDisplay(unit: ThresholdUnit, raw: string): number | null {
  const parsed = Number(raw.trim().replace(/[$,\s%]/g, ''))
  if (!Number.isFinite(parsed) || parsed < 0) return null
  if (unit === 'money' || unit === 'bps') return Math.round(parsed * 100)
  return Number.isInteger(parsed) ? parsed : null
}

function unitHint(unit: ThresholdUnit): string {
  switch (unit) {
    case 'money':
      return 'in dollars'
    case 'days':
      return 'in whole days'
    case 'count':
      return 'a whole number of open items'
    case 'bps':
      return 'as a percentage'
  }
}
